import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '../../../supabaseClient';
import { getNearbyDrivers } from '../services/deliveryServices';
import { useAuth } from '../../USER/Auth/AuthContext';
import { TRACKING_STATUSES, DEFAULT_MAP_CENTER } from '../../../constants';

// ── Iconos SVG (sin emojis, branding Bryemma) ──────────────────────────
const pickupIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:var(--bry-accent);color: var(--bry-text-main);width:32px;height:32px;border-radius:50%;
    display:flex;align-items:center;justify-content:center;border:3px solid #fff;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  </div>`,
  iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32],
});

const dropoffIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:var(--bry-bg-secondary);color: var(--bry-text-main);width:32px;height:32px;border-radius:50%;
    display:flex;align-items:center;justify-content:center;border:3px solid #fff;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
    </svg>
  </div>`,
  iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32],
});

// Moto SVG para conductores
const MOTO_SVG = `<svg width="16" height="12" viewBox="0 0 40 24" fill="white">
  <circle cx="8" cy="18" r="5" stroke="white" stroke-width="2" fill="none"/>
  <circle cx="32" cy="18" r="5" stroke="white" stroke-width="2" fill="none"/>
  <path d="M8 18 L12 8 L22 8 L28 14 L32 14" stroke="white" stroke-width="2" fill="none"/>
  <path d="M18 8 L22 4 L28 4" stroke="white" stroke-width="2" fill="none"/>
</svg>`;

const driverAvailableIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#00C853;width:30px;height:30px;border-radius:50%;
    display:flex;align-items:center;justify-content:center;border:3px solid #fff;
    box-shadow:0 2px 8px rgba(0,0,0,0.2);">${MOTO_SVG}</div>`,
  iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30],
});

const driverBusyIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#333333;width:30px;height:30px;border-radius:50%;
    display:flex;align-items:center;justify-content:center;border:3px solid #fff;
    box-shadow:0 2px 8px rgba(0,0,0,0.2);">${MOTO_SVG}</div>`,
  iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30],
});

const driverOfflineIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#9E9E9E;width:30px;height:30px;border-radius:50%;
    display:flex;align-items:center;justify-content:center;border:3px solid #fff;
    box-shadow:0 2px 8px rgba(0,0,0,0.2);">${MOTO_SVG}</div>`,
  iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -30],
});

function ClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      if (typeof onMapClick === 'function') {
        onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
      }
    },
  });
  return null;
}

function MapController({ pickupPos, dropoffPos, driverPositions, userLocation, centerOnUser = false }) {
  const map = useMap();

  useEffect(() => {
    const points = [];
    
    if (pickupPos) points.push([pickupPos.lat, pickupPos.lng]);
    if (dropoffPos) points.push([dropoffPos.lat, dropoffPos.lng]);
    if (userLocation && centerOnUser) points.push([userLocation.lat, userLocation.lng]);
    
    // Add driver positions to bounds
    driverPositions.forEach(driver => {
      if (driver.lat && driver.lng) {
        points.push([driver.lat, driver.lng]);
      }
    });

    if (points.length > 0) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [pickupPos, dropoffPos, driverPositions, userLocation, centerOnUser, map]);

  return null;
}

function RouteLine({ pickupPos, dropoffPos }) {
  const map = useMap();
  const routeRef = useRef(null);

  useEffect(() => {
    if (pickupPos && dropoffPos && map) {
      // Remove existing route
      if (routeRef.current) {
        map.removeLayer(routeRef.current);
      }

      // Create route line
      const routeCoords = [
        [pickupPos.lat, pickupPos.lng],
        [dropoffPos.lat, dropoffPos.lng]
      ];

      routeRef.current = L.polyline(routeCoords, {
        color: 'var(--bry-accent)',
        weight: 4,
        opacity: 0.7,
        dashArray: '10, 10',
        className: 'route-line'
      }).addTo(map);
    }

    return () => {
      if (routeRef.current && map) {
        map.removeLayer(routeRef.current);
      }
    };
  }, [pickupPos, dropoffPos, map]);

  return null;
}

function DriverMarker({ driver, isSelected, onSelect }) {
  const markerRef = useRef(null);
  const navigate = useNavigate();

  const getIcon = () => {
    switch (driver.status) {
      case TRACKING_STATUSES.ONLINE:
        return driverAvailableIcon;
      case TRACKING_STATUSES.ON_TRIP:
        return driverBusyIcon;
      case TRACKING_STATUSES.OFFLINE:
      default:
        return driverOfflineIcon;
    }
  };

  const getStatusColor = () => {
    switch (driver.status) {
      case TRACKING_STATUSES.ONLINE: return '#00C853';
      case TRACKING_STATUSES.ON_TRIP: return '#333333';
      case TRACKING_STATUSES.OFFLINE: return '#9E9E9E';
      default: return '#9E9E9E';
    }
  };

  const getStatusText = () => {
    switch (driver.status) {
      case TRACKING_STATUSES.ONLINE: return 'Disponible';
      case TRACKING_STATUSES.ON_TRIP: return 'En viaje';
      case TRACKING_STATUSES.OFFLINE: return 'Desconectado';
      default: return 'Desconocido';
    }
  };

  return (
    <Marker
      ref={markerRef}
      position={[driver.lat, driver.lng]}
      icon={getIcon()}
      eventHandlers={{
        click: () => onSelect(driver),
      }}
    >
      <Popup>
        <div className="driver-popup" style={{ minWidth: '200px' }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="mb-0 fw-bold">
              {driver.name || 'Conductor'}
            </h6>
            <div className="d-flex flex-column gap-1 align-items-end">
              <span 
                className="badge rounded-pill"
                style={{ backgroundColor: getStatusColor(), color: 'white' }}
              >
                {getStatusText()}
              </span>
              {driver.vehicle_type && (
                <span className="badge rounded-pill bg-dark text-white" style={{ fontSize: '0.7rem' }}>
                  {driver.vehicle_type.toUpperCase()}
                </span>
              )}
            </div>
          </div>
          
          <div className="mb-2">
            <small className="text-muted d-block">Distancia</small>
            <strong>{driver.distance_km?.toFixed(1) || 'N/A'} km</strong>
          </div>
          
          {driver.rating && (
            <div className="mb-2">
              <small className="text-muted d-block">Rating</small>
              <div className="d-flex align-items-center">
                <span className="text-warning me-1">⭐</span>
                <strong>{driver.rating.toFixed(1)}</strong>
                {driver.total_trips && (
                  <small className="text-muted ms-2">({driver.total_trips} viajes)</small>
                )}
              </div>
            </div>
          )}
          
          {driver.plate && (
            <div className="mb-2">
              <small className="text-muted d-block">Placa</small>
              <strong>{driver.plate}</strong>
            </div>
          )}
          
          <div className="d-flex gap-2">
            <button 
              className="btn btn-sm btn-outline-secondary flex-fill"
              onClick={() => navigate(`/conductor/${driver.driver_uid}`)}
            >
              Ver Perfil
            </button>
            <button 
              className="btn btn-sm btn-primary flex-fill"
              onClick={() => onSelect(driver)}
            >
              Seleccionar
            </button>
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

const FallbackMap = ({ 
  pickupPos, 
  dropoffPos, 
  onMapClick, 
  onDriverSelect,
  showDrivers = true,
  centerOnUser = false,
  height = '400px'
}) => {
  const { currentUser } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(DEFAULT_MAP_CENTER); // El Tigre, Venezuela
  const subscriptionRef = useRef(null);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          if (centerOnUser) {
            setMapCenter([location.lat, location.lng]);
          }
        },
        (error) => {
          if (error.code !== 1) {
            console.warn('Geolocation unavailable, using default center:', error.message);
          }
        }
      );
    }
  }, [centerOnUser]);

  const fetchDrivers = useCallback(async () => {
    if (!showDrivers) return;
    const center = pickupPos || { lat: DEFAULT_MAP_CENTER[0], lng: DEFAULT_MAP_CENTER[1] };
    try {
      setLoading(true);
      const result = await getNearbyDrivers(center.lat, center.lng);
      setDrivers(result || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [pickupPos, showDrivers]);

  // ISS-02: Safety timeout — if loading is still true after 8s, force it off
  // This prevents infinite spinner when getNearbyDrivers fails silently (RLS/network)
  useEffect(() => {
    if (!loading) return;
    const safetyTimer = setTimeout(() => {
      setLoading(false);
    }, 8000);
    return () => clearTimeout(safetyTimer);
  }, [loading]);

  // Simple subscription for driver updates using subscriptionRef
  useEffect(() => {
    if (!showDrivers) return;

    const subscription = supabase
      .channel('fallback-driver-tracking')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'drivers_tracking'
      }, (payload) => {

        fetchDrivers();
      })
      .subscribe();

    subscriptionRef.current = subscription;

    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [showDrivers, fetchDrivers]);

  // Fetch drivers when pickup position changes
  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  // Auto-refresh drivers every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchDrivers, 30000);
    return () => clearInterval(interval);
  }, [fetchDrivers]);

  const handleDriverSelect = (driver) => {
    setSelectedDriver(driver);
    if (onDriverSelect) {
      onDriverSelect(driver);
    }
    // Log selection for analytics if user is logged in
    if (currentUser) {

    }
  };

  return (
    <div style={{ height, position: 'relative' }}>
      <MapContainer
        center={mapCenter}
        zoom={13}
        style={{ height: '100%', width: '100%', borderRadius: '12px' }}
        zoomControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        <ClickHandler onMapClick={onMapClick} />
        
        <MapController 
          pickupPos={pickupPos}
          dropoffPos={dropoffPos}
          driverPositions={drivers}
          userLocation={userLocation}
          centerOnUser={centerOnUser}
        />

        {/* Route line */}
        <RouteLine pickupPos={pickupPos} dropoffPos={dropoffPos} />

        {/* Pickup marker */}
        {pickupPos && (
          <Marker position={[pickupPos.lat, pickupPos.lng]} icon={pickupIcon}>
            <Popup>
              <div>
                <h6 className="mb-1">📍 Punto de recogida</h6>
                <p className="mb-0 text-muted">
                  {pickupPos.address || 'Ubicación seleccionada'}
                </p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Dropoff marker */}
        {dropoffPos && (
          <Marker position={[dropoffPos.lat, dropoffPos.lng]} icon={dropoffIcon}>
            <Popup>
              <div>
                <h6 className="mb-1">🏁 Destino</h6>
                <p className="mb-0 text-muted">
                  {dropoffPos.address || 'Ubicación seleccionada'}
                </p>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Driver markers */}
        {showDrivers && drivers.map(driver => (
          <DriverMarker
            key={driver.driver_uid}
            driver={driver}
            isSelected={selectedDriver?.driver_uid === driver.driver_uid}
            onSelect={handleDriverSelect}
          />
        ))}

        {/* User location marker */}
        {userLocation && (
          <Marker position={[userLocation.lat, userLocation.lng]} icon={new L.DivIcon({
            className: 'custom-marker',
            html: `<div style="background:var(--bry-accent);width:22px;height:22px;border-radius:50%;
              border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>`,
            iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -22],
          })}>
            <Popup>
              <div>
                <h6 className="mb-1">Tu ubicacion</h6>
                <p className="mb-0 text-muted">Ubicacion actual</p>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Loading overlay */}
      {loading && (
        <div 
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '12px' }}
        >
          <div className="text-center">
            <div className="spinner-border text-primary mb-2" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <small>Buscando conductores...</small>
          </div>
        </div>
      )}

      {/* Driver count indicator */}
      {showDrivers && (
        <div 
          className="position-absolute top-2 end-2 bg-white rounded-pill px-3 py-2 shadow-sm"
          style={{ zIndex: 1000 }}
        >
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle me-1"
                style={{ 
                  width: '8px', 
                  height: '8px', 
                  backgroundColor: '#00C853' 
                }}
              />
              <small>{drivers.filter(d => d.status === TRACKING_STATUSES.ONLINE).length}</small>
            </div>
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle me-1"
                style={{ 
                  width: '8px', 
                  height: '8px', 
                  backgroundColor: '#333333' 
                }}
              />
              <small>{drivers.filter(d => d.status === TRACKING_STATUSES.ON_TRIP).length}</small>
            </div>
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle me-1"
                style={{ 
                  width: '8px', 
                  height: '8px', 
                  backgroundColor: '#9E9E9E' 
                }}
              />
              <small>{drivers.filter(d => d.status === TRACKING_STATUSES.OFFLINE).length}</small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FallbackMap;
